import {
  DateRange,
  TimelineTimeframes,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachWeekOfInterval,
  endOfQuarter,
  endOfWeek,
  format,
  getDaysInMonth,
  getWeek,
  startOfQuarter,
} from 'date-fns';

import { weekdaysNumberMap } from './constants';

export type TimelineColumn = {
  key: string;
  date: Timestamp;
  label: string;
  width: number;
  isStartOfYear: boolean;
};

export type GetTimelineColumnsOptions = {
  timeframe: TimelineTimeframes;
  dateRange: DateRange;
  weekStartsOn: WeekDays;
  dayWidth: number;
};

const columnsForMonth = (
  dateRange: DateRange,
  weekStartsOn: WeekDays,
  dayWidth: number,
): TimelineColumn[] => {
  const weekStartIndex = weekdaysNumberMap[weekStartsOn];
  return eachWeekOfInterval(
    { start: dateRange.startDate, end: dateRange.endDate },
    { weekStartsOn: weekStartIndex },
  ).map((date): TimelineColumn => {
    const weekNumber = getWeek(date, { weekStartsOn: weekStartIndex });
    const isStartOfYear = weekNumber === 1;
    return {
      key: format(date, 'yyyy-MM-dd'),
      date,
      label: isStartOfYear
        ? format(endOfWeek(date, { weekStartsOn: weekStartIndex }), 'yyyy')
        : format(date, 'LLL d'),
      width: 7 * dayWidth,
      isStartOfYear,
    };
  });
};

const columnsForMonthlyTimeframe = (
  dateRange: DateRange,
  dayWidth: number,
): TimelineColumn[] =>
  eachMonthOfInterval({
    start: dateRange.startDate,
    end: dateRange.endDate,
  }).map((date): TimelineColumn => {
    const isStartOfYear = date.getMonth() === 0;
    return {
      key: format(date, 'yyyy-MM-dd'),
      date,
      label: format(date, isStartOfYear ? 'yyyy' : 'MMM'),
      width: getDaysInMonth(date) * dayWidth,
      isStartOfYear,
    };
  });

const columnsForFiveYears = (
  dateRange: DateRange,
  dayWidth: number,
): TimelineColumn[] =>
  eachQuarterOfInterval({
    start: dateRange.startDate,
    end: dateRange.endDate,
  }).map((date): TimelineColumn => {
    const isStartOfYear = date.getMonth() === 0;
    const daysInQuarter = eachDayOfInterval({
      start: startOfQuarter(date),
      end: endOfQuarter(date),
    }).length;
    return {
      key: format(date, 'yyyy-MM-dd'),
      date,
      label: isStartOfYear ? format(date, 'yyyy') : '',
      width: daysInQuarter * dayWidth,
      isStartOfYear,
    };
  });

export const getTimelineColumns = ({
  timeframe,
  dateRange,
  weekStartsOn,
  dayWidth,
}: GetTimelineColumnsOptions): TimelineColumn[] => {
  if (timeframe === TimelineTimeframes.Month) {
    return columnsForMonth(dateRange, weekStartsOn, dayWidth);
  }

  if (timeframe === TimelineTimeframes.FiveYears) {
    return columnsForFiveYears(dateRange, dayWidth);
  }

  return columnsForMonthlyTimeframe(dateRange, dayWidth);
};
